import React from 'react';
import {
  FiEdit,
  FiClock,
  FiList,
  FiTarget,
  FiCalendar,
  FiBookOpen,
  FiUsers,
  FiPenTool,
} from 'react-icons/fi';
import IELTSCourseTips from '../../../components/IELTSCourseTips';

export default function WritingTips() {
  const overview = [
    { icon: <FiClock />, label: 'Time:', value: '60 minutes total' },
    { icon: <FiList />, label: 'Tasks:', value: '2 (Task 1: 150 words, Task 2: 250 words)' },
    { icon: <FiTarget />, label: 'Skills tested:', value: 'Task achievement, coherence, vocabulary, grammar' },
  ];

  const tips = [
    {
      title: 'Understand the Two Tasks',
      content: [
        '<strong>Task 1 (Academic):</strong> Describe a graph, chart, table, diagram, or process.',
        '<strong>Task 1 (General):</strong> Write a letter (formal, semi-formal, or informal).',
        '<strong>Task 2:</strong> Write an essay responding to a point of view, argument, or problem.',
      ],
      highlight: 'Tip: Task 2 is worth more marks, so spend 40 minutes on it and 20 minutes on Task 1.',
    },
    {
      title: 'Plan Before You Write',
      content: [
        'Spend 3-5 minutes planning your answer.',
        'Identify the key points you need to address.',
        'Create a brief outline of your paragraphs.',
      ],
      highlight: 'Tip: A clear plan leads to a well-organized essay.',
    },
    {
      title: 'Structure Your Writing',
      content: [
        '<strong>Task 1:</strong> Introduction → Overview → Details (2 body paragraphs).',
        '<strong>Task 2:</strong> Introduction → Body paragraphs (2-3) → Conclusion.',
      ],
      bullets: [
        'Each paragraph should have one main idea',
        'Use topic sentences at the start of paragraphs',
        'Link your ideas with cohesive devices',
      ],
      highlight: 'Tip: Examiners look for clear organization.',
    },
    {
      title: 'Use a Range of Vocabulary',
      content: [
        'Avoid repeating the same words.',
        'Use synonyms and paraphrasing.',
        'Learn topic-specific vocabulary for common themes.',
      ],
      highlight: 'Tip: Build your vocabulary with academic word lists.',
      linkText: 'View Vocabulary Lessons',
      linkPath: '/exams/english/#vocabulary',
      linkCategory: 'vocabulary',
    },
    {
      title: 'Demonstrate Grammatical Range',
      content: [
        'Use a mix of simple and complex sentences.',
        'Include different tenses appropriately.',
        'Use conditionals, passive voice, and relative clauses.',
      ],
      highlight: 'Tip: Accuracy is as important as range.',
      linkText: 'View Grammar Lessons',
      linkPath: '/exams/english/#grammar',
      linkCategory: 'grammar',
    },
    {
      title: 'Address the Task Fully',
      content: [
        'Answer all parts of the question.',
        'For Task 1: Include an overview and key features.',
        'For Task 2: Present a clear position and support it.',
      ],
      highlight: 'Tip: Underline key words in the question to stay on topic.',
    },
    {
      title: 'Manage Your Time',
      content: [
        'Task 1: 20 minutes (plan, write, check).',
        'Task 2: 40 minutes (plan, write, check).',
        'Leave 2-3 minutes at the end to review.',
      ],
      highlight: 'Tip: Practice writing under timed conditions.',
    },
    {
      title: 'Check Your Work',
      content: [
        'Review for spelling and grammar errors.',
        'Ensure you meet the word count requirements.',
        'Check that your paragraphs are well-connected.',
      ],
      highlight: 'Tip: Common errors to check: subject-verb agreement, articles, prepositions.',
    },
  ];

  const extraStrategies = [
    'Read model essays to understand good structure and vocabulary.',
    'Practice describing different types of graphs and charts.',
    'Build a bank of useful phrases for introductions and conclusions.',
    "Don't memorize essays — examiners can tell and will penalize this.",
    'Focus on clarity over complexity if you make many grammar mistakes.',
  ];

  const improvementTips = [
    { icon: <FiCalendar />, label: 'Daily practice:', text: 'Write at least one Task 1 or Task 2 response daily.' },
    { icon: <FiBookOpen />, label: 'Read widely:', text: 'Read newspapers, essays, and reports to improve vocabulary.' },
    { icon: <FiPenTool />, label: 'Get feedback:', text: 'Have your writing checked by a teacher or use AI feedback.' },
    { icon: <FiUsers />, label: 'Study with others:', text: 'Compare essays with study partners to learn different approaches.' },
  ];

  const aiHelp = {
    title: 'Need help with IELTS Writing strategies?',
    firstMessage: 'I need help improving my IELTS Writing score. Can you help me with essay structure and vocabulary?',
    context: 'IELTS Writing: 60 minutes, 2 tasks. Task 1: 150 words minimum (describe data or write a letter). Task 2: 250 words minimum (write an essay). Assessed on: Task Achievement, Coherence & Cohesion, Lexical Resource, Grammatical Range & Accuracy.',
  };

  return (
    <IELTSCourseTips
      category="writing"
      icon={<FiEdit />}
      title="IELTS Writing: What You Should Know"
      overview={overview}
      tips={tips}
      extraStrategies={extraStrategies}
      improvementTips={improvementTips}
      aiHelp={aiHelp}
    />
  );
}
